import React from "react";
import { Button, ButtonSize, TextField } from "@bale/ui";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import {
  GeneralErrors,
  GeneralValidators,
  useInputHandler,
} from "hooks/useInputHandler";
import { strings } from "intl/fa";
import styles from "./Profile.module.scss";
import Footer from "../../commons/footer/Footer";
import { disableButton } from "utils/utils";
import ImageInput from "components/commons/ImageInput/ImageInput";
import {
  FileErrors,
  FileValidators,
  useFileHandler,
} from "hooks/useFileHandler";
import { AccountType } from "../../../core/auth/auth.entity";

const PostcodeLength = 10;
const FileMaxSize = 10000000; //kb

interface Props {
  userType: AccountType;
  buttonText: string;
}

const ProfileInputsCard: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const name = useInputHandler({
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const organization = useInputHandler({
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const nationalCode = useInputHandler({
    numberOnly: true,
    rules: [GeneralValidators.required, GeneralValidators.nationalCode],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.NationalCode]: strings.profile.national_code.error.invalid,
    },
  });
  const economicCode = useInputHandler({
    numberOnly: true,
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const phone = useInputHandler({
    numberOnly: true,
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const address = useInputHandler({
    rules: [GeneralValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
    },
  });
  const postcode = useInputHandler({
    numberOnly: true,
    rules: [
      GeneralValidators.required,
      GeneralValidators.fixedLength(PostcodeLength),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [GeneralErrors.FixedLength]: strings.profile.postcode.error,
    },
  });
  const establishmentCertificate = useFileHandler({
    rules: [FileValidators.maximumSize(FileMaxSize), FileValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.image_max_size,
    },
  });
  const changesNotice = useFileHandler({
    rules: [FileValidators.maximumSize(FileMaxSize), FileValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.image_max_size,
    },
  });
  const activityPermission = useFileHandler({
    rules: [FileValidators.maximumSize(FileMaxSize), FileValidators.required],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.image_max_size,
    },
  });
  const isLegalUser = props.userType === AccountType.Organization;
  return (
    <>
      <CardWrapper className={styles.Card}>
        <TextField
          label={strings.register.name.label}
          placeholder={strings.register.name.placeholder}
          dir={"rtl"}
          {...name}
        />
        {isLegalUser && (
          <TextField
            label={strings.profile.organization.label}
            placeholder={strings.profile.organization.placeholder}
            dir={"rtl"}
            {...organization}
          />
        )}
        <TextField
          label={
            isLegalUser
              ? strings.profile.national_code.label
              : strings.profile.national_code.legal_label
          }
          placeholder={strings.profile.national_code.placeholder}
          dir={"ltr"}
          {...nationalCode}
        />
        {isLegalUser && (
          <>
            <TextField
              label={strings.profile.economic_code.label}
              placeholder={strings.profile.economic_code.placeholder}
              dir={"ltr"}
              {...economicCode}
            />
            <TextField
              label={strings.profile.phone.label}
              placeholder={strings.profile.phone.placeholder}
              dir={"ltr"}
              {...phone}
            />
          </>
        )}
        <TextField
          label={strings.profile.address.label}
          placeholder={strings.profile.address.placeholder}
          dir={"rtl"}
          multiline
          {...address}
        />
        <TextField
          label={strings.profile.postcode.label}
          placeholder={strings.profile.postcode.placeholder}
          dir={"ltr"}
          maxLength={PostcodeLength}
          {...postcode}
        />
        {isLegalUser && (
          <>
            <ImageInput
              title={strings.profile.image_inputs.establishment_certificate}
              {...establishmentCertificate}
            />
            <ImageInput
              title={strings.profile.image_inputs.changes_notice}
              {...changesNotice}
            />
            <ImageInput
              title={strings.profile.image_inputs.activity_permission}
              {...activityPermission}
            />
          </>
        )}
      </CardWrapper>
      <Footer>
        <Button
          text={props.buttonText}
          size={ButtonSize.XLarge}
          disable={disableButton(
            name,
            nationalCode,
            address,
            postcode,
            ...(isLegalUser
              ? [
                  establishmentCertificate,
                  changesNotice,
                  activityPermission,
                  economicCode,
                  phone,
                ]
              : [])
          )}
        />
      </Footer>
    </>
  );
};

export default ProfileInputsCard;
